import {
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import useWebSocket from './use-websocket';

import AuthContext from '@/contexts/auth';
import { useNotifications } from '@/contexts/notifications';
import { getTokens } from '@/lib/utils';
import type { ConnectionRequestDetail } from '@/pages/RelatedParties/types';

interface WsUser {
    id?: number;
    username?: string;
    email?: string;
    business_name?: string;
    profile_image?: string | null;
}

interface WsMessage {
    event?: string;
    removed_by?: number;
    connection_request?: {
        id?: number;
        from_user?: WsUser;
        to_user?: WsUser;
        status?: string;
        is_email_sent?: boolean;
        created_at?: string;
        updated_at?: string;
    };
}

interface CachedResponse {
    data?: {
        results?: ConnectionRequestDetail[];
    };
}

function buildWsUrl(): string | null {
    const { accessToken } = getTokens();
    if (!accessToken) return null;

    const serverUrl = import.meta.env
        .VITE_APP_SERVER_URL as string;
    if (!serverUrl) return null;

    // https://host/api → wss://host/ws/notifications/
    const wsBase = serverUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://')
        .replace(/\/api\/?$/, '');

    return (
        `${wsBase}/ws/notifications/`
        + `?token=${accessToken}`
    );
}

export default function useConnectionNotifications() {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { state } = useContext(AuthContext);

    // Deduplicate WS events by tracking recently handled event+id pairs
    const handledRef = useRef<Map<string, number>>(new Map());

    // Recompute WebSocket URL when auth state changes (login/logout/token refresh)
    const wsUrl = useMemo(() => buildWsUrl(), [state]);

    const handleMessage = useCallback(
        (raw: unknown) => {
            const msg = raw as WsMessage;
            const req = msg?.connection_request;
            const fromUser = req?.from_user;
            const toUser = req?.to_user;

            // Deduplicate: skip if same event+id was handled within 5s
            const dedupeKey = `${msg?.event}:${req?.id ?? msg?.removed_by ?? ''}`;
            const now = Date.now();
            const lastHandled = handledRef.current.get(dedupeKey);
            if (lastHandled && now - lastHandled < 5000) {
                return;
            }
            handledRef.current.set(dedupeKey, now);
            // Clean old entries
            Array.from(handledRef.current.entries())
                .filter(([, ts]) => now - ts > 10000)
                .forEach(([key]) => {
                    handledRef.current.delete(key);
                });

            if (msg?.event === 'connection_request_received') {
                const name = fromUser?.business_name
                    || fromUser?.username
                    || 'Someone';

                toast.info(
                    `New connection request from ${name}`,
                );
                addNotification({
                    type: 'connection_request',
                    title: 'Connection Request',
                    message: `${name} sent you a connection request.`,
                });

                // Optimistically add to received cache
                if (req) {
                    queryClient.setQueryData<CachedResponse>(
                        ['connection-requests-received'],
                        (old) => {
                            const results = old?.data?.results;
                            if (!Array.isArray(results)) {
                                return old;
                            }
                            const exists = results.some(
                                (r) => r.id === req.id,
                            );
                            if (exists) return old;
                            return {
                                ...old,
                                data: {
                                    ...old?.data,
                                    results: [
                                        req as ConnectionRequestDetail,
                                        ...results,
                                    ],
                                },
                            };
                        },
                    );
                }
                // Always refetch to ensure consistency
                queryClient.invalidateQueries({
                    queryKey: [
                        'connection-requests-received',
                    ],
                });
            }

            if (msg?.event === 'connection_request_accepted') {
                // Event sent to the original sender; to_user is the acceptor
                const name = toUser?.business_name
                    || toUser?.username
                    || 'Someone';

                toast.success(
                    `${name} accepted your connection request`,
                );
                addNotification({
                    type: 'connection_accepted',
                    title: 'Connection Accepted',
                    message: `${name} accepted your connection request.`,
                });

                // Optimistically remove from sent cache
                if (req?.id) {
                    queryClient.setQueryData<CachedResponse>(
                        ['connection-requests-sent'],
                        (old) => {
                            const results = old?.data?.results;
                            if (!Array.isArray(results)) {
                                return old;
                            }
                            return {
                                ...old,
                                data: {
                                    ...old?.data,
                                    results: results.filter(
                                        (r) => r.id !== req.id,
                                    ),
                                },
                            };
                        },
                    );
                }
                queryClient.invalidateQueries({
                    queryKey: [
                        'connection-requests-sent',
                    ],
                });
                queryClient.invalidateQueries({
                    queryKey: ['connections'],
                });
            }

            if (msg?.event === 'connection_request_rejected') {
                // Event sent to the original sender; to_user is the rejector
                const name = toUser?.business_name
                    || toUser?.username
                    || 'Someone';

                toast.info(
                    `${name} declined your connection request`,
                );
                addNotification({
                    type: 'connection_declined',
                    title: 'Connection Declined',
                    message: `${name} declined your connection request.`,
                });

                // Optimistically remove from sent cache
                if (req?.id) {
                    queryClient.setQueryData<CachedResponse>(
                        ['connection-requests-sent'],
                        (old) => {
                            const results = old?.data?.results;
                            if (!Array.isArray(results)) {
                                return old;
                            }
                            return {
                                ...old,
                                data: {
                                    ...old?.data,
                                    results: results.filter(
                                        (r) => r.id !== req.id,
                                    ),
                                },
                            };
                        },
                    );
                }
                queryClient.invalidateQueries({
                    queryKey: [
                        'connection-requests-sent',
                    ],
                });
            }

            if (msg?.event === 'connection_request_cancelled') {
                // Sender cancelled their request — update received list
                const name = fromUser?.business_name
                    || fromUser?.username
                    || 'Someone';

                toast.info(
                    `${name} cancelled their connection request`,
                );

                if (req?.id) {
                    queryClient.setQueryData<CachedResponse>(
                        ['connection-requests-received'],
                        (old) => {
                            const results = old?.data?.results;
                            if (!Array.isArray(results)) {
                                return old;
                            }
                            return {
                                ...old,
                                data: {
                                    ...old?.data,
                                    results: results.filter(
                                        (r) => r.id !== req.id,
                                    ),
                                },
                            };
                        },
                    );
                }
                queryClient.invalidateQueries({
                    queryKey: [
                        'connection-requests-received',
                    ],
                });
            }

            if (msg?.event === 'connection_removed') {
                toast.info('A connection was removed');
                addNotification({
                    type: 'connection_removed',
                    title: 'Connection Removed',
                    message: 'A connection has been removed.',
                });
                queryClient.invalidateQueries({
                    queryKey: ['connections'],
                });
            }

            // Refresh navbar notifications on any event
            if (msg?.event) {
                queryClient.invalidateQueries({
                    queryKey: ['notifications'],
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        'notifications-unread-count',
                    ],
                });
            }
        },
        [queryClient, addNotification],
    );

    return useWebSocket({
        url: wsUrl || '',
        onMessage: handleMessage,
        enabled: !!wsUrl,
    });
}
